import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {ArrowBack as ArrowBackIcon, Cancel as CancelIcon, CheckCircle as CheckCircleIcon, Event as EventIcon, Group as GroupIcon, Person as PersonIcon,} from '@mui/icons-material';
import eventService from '../../services/eventService';

const EventDetails = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [statusFormData, setStatusFormData] = useState({
        status: '',
        notes: '',
    });

    const loadEvent = useCallback(async () => {
        try {
            setLoading(true);
            const data = await eventService.getEventById(id);
            setEvent(data);
        } catch (err) {
            console.error('Fehler beim Laden des Events:', err);
            setError('Fehler beim Laden des Events: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadEvent();
    }, [loadEvent]);

    const handleOpenStatusDialog = (participant) => {
        setSelectedParticipant(participant);
        setStatusFormData({
            status: participant.status,
            notes: participant.notes || '',
        });
        setOpenStatusDialog(true);
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedParticipant(null);
        setStatusFormData({
            status: '',
            notes: '',
        });
    };

    const handleUpdateStatus = async () => {
        try {
            await eventService.updateParticipantStatus(selectedParticipant.id, statusFormData);
            setSuccess('Status erfolgreich aktualisiert!');
            handleCloseStatusDialog();
            loadEvent();
        } catch (err) {
            setError('Fehler beim Aktualisieren des Status: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleQuickStatusChange = async (participantId, currentStatus, newStatus) => {
        if (currentStatus === newStatus) {
            newStatus = 'UNKNOWN'; // Toggle off if the same status is clicked
        }
        try {
            await eventService.updateParticipantStatus(participantId, {
                status: newStatus,
                notes: ''
            });

            // Aktualisiere nur den lokalen State, ohne die Seite neu zu laden
            setEvent(prevEvent => ({
                ...prevEvent,
                participants: prevEvent.participants.map(p =>
                    p.id === participantId
                        ? {...p, status: newStatus}
                        : p
                ),
                // Aktualisiere die Zähler
                presentCount: prevEvent.participants.filter(p =>
                    p.id === participantId ? newStatus === 'PRESENT' : p.status === 'PRESENT'
                ).length,
                absentCount: prevEvent.participants.filter(p =>
                    p.id === participantId ? newStatus === 'ABSENT' : p.status === 'ABSENT'
                ).length,
                excusedCount: prevEvent.participants.filter(p =>
                    p.id === participantId ? newStatus === 'EXCUSED' : p.status === 'EXCUSED'
                ).length
            }));

            setSuccess('Status erfolgreich aktualisiert!');
            // Entferne die Success-Message nach 2 Sekunden
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            setError('Fehler beim Aktualisieren des Status: ' + (err.response?.data?.message || err.message));
        }
    };


    if (loading) {
        return (
            <Container maxWidth="lg" sx={{mt: 4}}>
                <Typography>Lade Event-Details...</Typography>
            </Container>
        );
    }

    if (!event) {
        return (
            <Container maxWidth="lg" sx={{mt: 4}}>
                <Alert severity="error">Event nicht gefunden</Alert>
                <Button onClick={() => navigate('/events')} sx={{mt: 2}}>
                    Zurück zur Übersicht
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
            <Button
                startIcon={<ArrowBackIcon/>}
                onClick={() => navigate('/events')}
                sx={{mb: 3}}
            >
                Zurück zur Übersicht
            </Button>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Event Details Card */}
            <Card sx={{mb: 4}}>
                <CardContent>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
                        <EventIcon sx={{fontSize: 40, color: 'primary.main'}}/>
                        <Box sx={{flexGrow: 1}}>
                            <Typography variant="h4" gutterBottom>
                                {event.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {event.unit?.name}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="body2" color="textSecondary">
                                Datum:
                            </Typography>
                            <Typography variant="body1">
                                {new Date(event.eventDate).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}, {new Date(event.eventDate).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                                {event.eventEndDate && (
                                    <> - {new Date(event.eventEndDate).toLocaleTimeString('de-DE', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}</>
                                )}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="textSecondary">
                                Ort:
                            </Typography>
                            <Typography variant="body1">
                                {event.location || '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="textSecondary">
                                Erstellt von:
                            </Typography>
                            <Typography variant="body1">
                                {event.createdByName}
                            </Typography>
                        </Box>
                    </Box>

                    {event.description && (
                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="textSecondary">
                                Beschreibung:
                            </Typography>
                            <Typography variant="body1">
                                {event.description}
                            </Typography>
                        </Box>
                    )}

                    {/* Statistics */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: {xs: 1, sm: 2},
                        mt: 3
                    }}>
                        <Paper sx={{
                            p: {xs: 1, sm: 2},
                            textAlign: 'center',
                            bgcolor: 'primary.50',
                            border: {xs: '1.5px solid', sm: '2px solid'},
                            borderColor: 'primary.main'
                        }}>
                            <Typography
                                variant="h4"
                                color="primary.main"
                                fontWeight="bold"
                                sx={{fontSize: {xs: '1.5rem', sm: '2.125rem'}}}
                            >
                                {event.totalParticipants || 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="primary.main"
                                fontWeight={600}
                                sx={{fontSize: {xs: '0.625rem', sm: '0.875rem'}}}
                            >
                                Gesamt
                            </Typography>
                        </Paper>
                        <Paper sx={{
                            p: {xs: 1, sm: 2},
                            textAlign: 'center',
                            bgcolor: 'success.50',
                            border: {xs: '1.5px solid', sm: '2px solid'},
                            borderColor: 'success.main'
                        }}>
                            <Typography
                                variant="h4"
                                color="success.main"
                                fontWeight="bold"
                                sx={{fontSize: {xs: '1.5rem', sm: '2.125rem'}}}
                            >
                                {event.presentCount || 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="success.main"
                                fontWeight={600}
                                sx={{fontSize: {xs: '0.625rem', sm: '0.875rem'}}}
                            >
                                Anwesend
                            </Typography>
                        </Paper>
                        <Paper sx={{
                            p: {xs: 1, sm: 2},
                            textAlign: 'center',
                            bgcolor: 'warning.50',
                            border: {xs: '1.5px solid', sm: '2px solid'},
                            borderColor: 'warning.main'
                        }}>
                            <Typography
                                variant="h4"
                                color="warning.main"
                                fontWeight="bold"
                                sx={{fontSize: {xs: '1.5rem', sm: '2.125rem'}}}
                            >
                                {event.excusedCount || 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="warning.main"
                                fontWeight={600}
                                sx={{fontSize: {xs: '0.625rem', sm: '0.875rem'}}}
                            >
                                Entsch.
                            </Typography>
                        </Paper>
                        <Paper sx={{
                            p: {xs: 1, sm: 2},
                            textAlign: 'center',
                            bgcolor: 'error.50',
                            border: {xs: '1.5px solid', sm: '2px solid'},
                            borderColor: 'error.main'
                        }}>
                            <Typography
                                variant="h4"
                                color="error.main"
                                fontWeight="bold"
                                sx={{fontSize: {xs: '1.5rem', sm: '2.125rem'}}}
                            >
                                {event.absentCount || 0}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="error.main"
                                fontWeight={600}
                                sx={{fontSize: {xs: '0.625rem', sm: '0.875rem'}}}
                            >
                                Abwesend
                            </Typography>
                        </Paper>
                    </Box>
                </CardContent>
            </Card>

            {/* Members Table */}
            <Box sx={{mb: 4}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                    <PersonIcon color="primary"/>
                    <Typography variant="h5">
                        Mitglieder
                    </Typography>
                    <Chip
                        label={event.participants?.filter(p => p.memberId).length || 0}
                        color="default"
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Anwesend</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>A</Box>
                                    </strong>
                                </TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Entschuldigt</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>E</Box>
                                    </strong>
                                </TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Abwesend</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>N</Box>
                                    </strong>
                                </TableCell>
                                <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}><strong>Notizen</strong></TableCell>
                                <TableCell align="right" sx={{display: {xs: 'none', sm: 'table-cell'}}}><strong>Aktionen</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {event.participants?.filter(p => p.memberId).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="textSecondary" sx={{py: 4}}>
                                            Keine Mitglieder vorhanden
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                event.participants?.filter(p => p.memberId).map((participant) => (
                                    <TableRow key={participant.id} hover>
                                        <TableCell>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <PersonIcon fontSize="small" color="primary"/>
                                                <Typography variant="body1">
                                                    {participant.memberName}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'PRESENT'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'PRESENT')}
                                                color="success"
                                                icon={<CheckCircleIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CheckCircleIcon sx={{color: 'success.main'}}/>}
                                                title="Anwesend"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'EXCUSED'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'EXCUSED')}
                                                color="warning"
                                                icon={<CancelIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CancelIcon sx={{color: 'warning.main'}}/>}
                                                title="Entschuldigt"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'ABSENT'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'ABSENT')}
                                                color="error"
                                                icon={<CancelIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CancelIcon sx={{color: 'error.main'}}/>}
                                                title="Abwesend"
                                            />
                                        </TableCell>
                                        <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>
                                            <Typography variant="body2" color="textSecondary">
                                                {participant.notes || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{display: {xs: 'none', sm: 'table-cell'}}}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleOpenStatusDialog(participant)}
                                            >
                                                Bearbeiten
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Users/Betreuer Table */}
            <Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                    <GroupIcon color="secondary"/>
                    <Typography variant="h5">
                        Betreuer
                    </Typography>
                    <Chip
                        label={event.participants?.filter(p => !p.memberId).length || 0}
                        color="default"
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Anwesend</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>A</Box>
                                    </strong>
                                </TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Entschuldigt</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>E</Box>
                                    </strong>
                                </TableCell>
                                <TableCell align="center">
                                    <strong>
                                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>Abwesend</Box>
                                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>N</Box>
                                    </strong>
                                </TableCell>
                                <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}><strong>Notizen</strong></TableCell>
                                <TableCell align="right" sx={{display: {xs: 'none', sm: 'table-cell'}}}><strong>Aktionen</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {event.participants?.filter(p => !p.memberId).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="textSecondary" sx={{py: 4}}>
                                            Keine Betreuer vorhanden
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                event.participants?.filter(p => !p.memberId).map((participant) => (
                                    <TableRow key={participant.id} hover>
                                        <TableCell>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <GroupIcon fontSize="small" color="secondary"/>
                                                <Typography variant="body1">
                                                    {participant.userName}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'PRESENT'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'PRESENT')}
                                                color="success"
                                                icon={<CheckCircleIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CheckCircleIcon sx={{color: 'success.main'}}/>}
                                                title="Anwesend"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'EXCUSED'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'EXCUSED')}
                                                color="warning"
                                                icon={<CancelIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CancelIcon sx={{color: 'warning.main'}}/>}
                                                title="Entschuldigt"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={participant.status === 'ABSENT'}
                                                onChange={() => handleQuickStatusChange(participant.id, participant.status, 'ABSENT')}
                                                color="error"
                                                icon={<CancelIcon sx={{color: 'grey.300'}}/>}
                                                checkedIcon={<CancelIcon sx={{color: 'error.main'}}/>}
                                                title="Abwesend"
                                            />
                                        </TableCell>
                                        <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>
                                            <Typography variant="body2" color="textSecondary">
                                                {participant.notes || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{display: {xs: 'none', sm: 'table-cell'}}}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleOpenStatusDialog(participant)}
                                            >
                                                Bearbeiten
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Status Update Dialog */}
            <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Status ändern: {selectedParticipant?.memberName || selectedParticipant?.userName}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, mt: 2}}>
                        <Box>
                            <Typography variant="subtitle2" color="primary" gutterBottom sx={{fontWeight: 600, mb: 2}}>
                                Status auswählen
                            </Typography>
                            <RadioGroup
                                value={statusFormData.status}
                                onChange={(e) => setStatusFormData({...statusFormData, status: e.target.value})}
                            >
                                <FormControlLabel
                                    value="PRESENT"
                                    control={<Radio color="success"/>}
                                    label={
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5}}>
                                            <CheckCircleIcon sx={{color: 'success.main'}}/>
                                            <Box>
                                                <Typography variant="body1" fontWeight={500}>
                                                    Anwesend
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Teilnehmer war bei der Veranstaltung anwesend
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{
                                        border: '2px solid',
                                        borderColor: statusFormData.status === 'PRESENT' ? 'success.main' : 'grey.300',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        mb: 1,
                                        bgcolor: statusFormData.status === 'PRESENT' ? 'success.50' : 'transparent',
                                        '&:hover': {
                                            bgcolor: 'success.50',
                                            borderColor: 'success.main',
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value="EXCUSED"
                                    control={<Radio color="warning"/>}
                                    label={
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5}}>
                                            <CancelIcon sx={{color: 'warning.main'}}/>
                                            <Box>
                                                <Typography variant="body1" fontWeight={500}>
                                                    Entschuldigt
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Teilnehmer war entschuldigt abwesend
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{
                                        border: '2px solid',
                                        borderColor: statusFormData.status === 'EXCUSED' ? 'warning.main' : 'grey.300',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        mb: 1,
                                        bgcolor: statusFormData.status === 'EXCUSED' ? 'warning.50' : 'transparent',
                                        '&:hover': {
                                            bgcolor: 'warning.50',
                                            borderColor: 'warning.main',
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value="ABSENT"
                                    control={<Radio color="error"/>}
                                    label={
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5}}>
                                            <CancelIcon sx={{color: 'error.main'}}/>
                                            <Box>
                                                <Typography variant="body1" fontWeight={500}>
                                                    Abwesend
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Teilnehmer war unentschuldigt abwesend
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{
                                        border: '2px solid',
                                        borderColor: statusFormData.status === 'ABSENT' ? 'error.main' : 'grey.300',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        mb: 1,
                                        bgcolor: statusFormData.status === 'ABSENT' ? 'error.50' : 'transparent',
                                        '&:hover': {
                                            bgcolor: 'error.50',
                                            borderColor: 'error.main',
                                        }
                                    }}
                                />
                            </RadioGroup>
                        </Box>
                        <TextField
                            label="Notizen (optional)"
                            value={statusFormData.notes}
                            onChange={(e) => setStatusFormData({...statusFormData, notes: e.target.value})}
                            multiline
                            rows={3}
                            fullWidth
                            placeholder="z.B. Grund der Abwesenheit, besondere Vorkommnisse..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button onClick={handleCloseStatusDialog} color="inherit">
                        Abbrechen
                    </Button>
                    <Button onClick={handleUpdateStatus} variant="contained" size="large">
                        Status speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default EventDetails;

